import axios from "axios";
import { API_BASE_URL } from "@/lib/apiBase";

/**
 * Resolve a possibly-relative avatar URL against the API origin.
 * External HTTPS URLs are returned as-is.
 */
export function resolveAvatarUrl(url) {
  if (!url) return null;
  const trimmed = String(url).trim();
  if (!trimmed) return null;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (trimmed.startsWith("//")) {
    if (typeof window !== "undefined" && window.location?.protocol) {
      return `${window.location.protocol}${trimmed}`;
    }
    return `https:${trimmed}`;
  }
  const path = trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
  return `${API_BASE_URL}${path}`;
}

function authHeaders(accessToken, extra = {}) {
  return {
    Authorization: `Bearer ${accessToken}`,
    ...extra,
  };
}

export async function setOrgAvatarUrl(accessToken, orgId, url) {
  const res = await axios.patch(
    `${API_BASE_URL}/org/${orgId}/avatar/url`,
    { url },
    { headers: authHeaders(accessToken) }
  );
  return res.data;
}

export async function uploadOrgAvatar(accessToken, orgId, file) {
  const form = new FormData();
  form.append("file", file);
  const res = await axios.post(
    `${API_BASE_URL}/org/${orgId}/avatar/upload`,
    form,
    {
      headers: authHeaders(accessToken, {
        "Content-Type": "multipart/form-data",
      }),
    }
  );
  return res.data;
}

export async function clearOrgAvatar(accessToken, orgId) {
  const res = await axios.delete(`${API_BASE_URL}/org/${orgId}/avatar`, {
    headers: authHeaders(accessToken),
  });
  return res.data;
}

export async function setMemberAvatarUrl(accessToken, orgId, userId, url) {
  const res = await axios.patch(
    `${API_BASE_URL}/org/${orgId}/members/${userId}/avatar/url`,
    { url },
    { headers: authHeaders(accessToken) }
  );
  return res.data;
}

export async function uploadMemberAvatar(accessToken, orgId, userId, file) {
  const form = new FormData();
  form.append("file", file);
  const res = await axios.post(
    `${API_BASE_URL}/org/${orgId}/members/${userId}/avatar/upload`,
    form,
    {
      headers: authHeaders(accessToken, {
        "Content-Type": "multipart/form-data",
      }),
    }
  );
  return res.data;
}

export async function clearMemberAvatar(accessToken, orgId, userId) {
  const res = await axios.delete(
    `${API_BASE_URL}/org/${orgId}/members/${userId}/avatar`,
    { headers: authHeaders(accessToken) }
  );
  return res.data;
}

export async function setIncomingWebhookAvatarUrl(
  accessToken,
  orgId,
  webhookId,
  url
) {
  const res = await axios.patch(
    `${API_BASE_URL}/org/${orgId}/connectors/incoming/${webhookId}/avatar/url`,
    { url },
    { headers: authHeaders(accessToken) }
  );
  return res.data;
}

export async function uploadIncomingWebhookAvatar(
  accessToken,
  orgId,
  webhookId,
  file
) {
  const form = new FormData();
  form.append("file", file);
  const res = await axios.post(
    `${API_BASE_URL}/org/${orgId}/connectors/incoming/${webhookId}/avatar/upload`,
    form,
    {
      headers: authHeaders(accessToken, {
        "Content-Type": "multipart/form-data",
      }),
    }
  );
  return res.data;
}

export async function clearIncomingWebhookAvatar(
  accessToken,
  orgId,
  webhookId
) {
  const res = await axios.delete(
    `${API_BASE_URL}/org/${orgId}/connectors/incoming/${webhookId}/avatar`,
    { headers: authHeaders(accessToken) }
  );
  return res.data;
}

export async function setCircatronInstallationAvatarUrl(
  accessToken,
  orgId,
  installationId,
  url
) {
  const res = await axios.patch(
    `${API_BASE_URL}/org/${orgId}/connectors/installations/${installationId}/avatar/url`,
    { url },
    { headers: authHeaders(accessToken) }
  );
  return res.data;
}

export async function uploadCircatronInstallationAvatar(
  accessToken,
  orgId,
  installationId,
  file
) {
  const form = new FormData();
  form.append("file", file);
  const res = await axios.post(
    `${API_BASE_URL}/org/${orgId}/connectors/installations/${installationId}/avatar/upload`,
    form,
    {
      headers: authHeaders(accessToken, {
        "Content-Type": "multipart/form-data",
      }),
    }
  );
  return res.data;
}

export async function clearCircatronInstallationAvatar(
  accessToken,
  orgId,
  installationId
) {
  const res = await axios.delete(
    `${API_BASE_URL}/org/${orgId}/connectors/installations/${installationId}/avatar`,
    { headers: authHeaders(accessToken) }
  );
  return res.data;
}

export async function updateMemberTitle(accessToken, orgId, userId, title) {
  const res = await axios.patch(
    `${API_BASE_URL}/org/${orgId}/members/${userId}/title`,
    { title: title == null || title === "" ? null : title },
    { headers: authHeaders(accessToken) }
  );
  return res.data;
}

export async function setSocialAvatarUrl(accessToken, url) {
  const res = await axios.patch(
    `${API_BASE_URL}/social/me/profile/avatar/url`,
    { url },
    { headers: authHeaders(accessToken) }
  );
  return res.data;
}

export async function uploadSocialAvatar(accessToken, file) {
  const form = new FormData();
  form.append("file", file);
  const res = await axios.post(
    `${API_BASE_URL}/social/me/profile/avatar/upload`,
    form,
    {
      headers: authHeaders(accessToken, {
        "Content-Type": "multipart/form-data",
      }),
    }
  );
  return res.data;
}

export async function clearSocialAvatar(accessToken) {
  const res = await axios.delete(
    `${API_BASE_URL}/social/me/profile/avatar`,
    { headers: authHeaders(accessToken) }
  );
  return res.data;
}

export async function setSocialBannerUrl(accessToken, url) {
  const res = await axios.patch(
    `${API_BASE_URL}/social/me/profile/banner/url`,
    { url },
    { headers: authHeaders(accessToken) }
  );
  return res.data;
}

export async function uploadSocialBanner(accessToken, file) {
  const form = new FormData();
  form.append("file", file);
  const res = await axios.post(
    `${API_BASE_URL}/social/me/profile/banner/upload`,
    form,
    {
      headers: authHeaders(accessToken, {
        "Content-Type": "multipart/form-data",
      }),
    }
  );
  return res.data;
}

export async function clearSocialBanner(accessToken) {
  const res = await axios.delete(
    `${API_BASE_URL}/social/me/profile/banner`,
    { headers: authHeaders(accessToken) }
  );
  return res.data;
}
